import { Outlet, useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useMenu } from "../../contexts/MenuContext";

import NavBar from "../navbar/NavBar";
import {
  Drawer,
  Layout as LayoutAntd,
  Menu,
  Space,
  Typography,
} from "antd";
import styles from "./Layout.module.scss";
import logo from "./../../assets/logo.jpg";
import logoSmall from "./../../assets/logo_small.jpeg";
import { useTranslation } from "react-i18next";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  getOrganizationBySubdomain,
  hasWorkingOrganization,
} from "../../api/Helper";
import { LuUpload } from "react-icons/lu";
import { FaRegBuilding } from "react-icons/fa";
import { FiUsers } from "react-icons/fi";
import { GrCatalog } from "react-icons/gr";
import { LiaStoreSolid } from "react-icons/lia";
import { IoSettingsOutline } from "react-icons/io5";
import {
  RxBarChart,
  RxBookmark,
  RxBox,
  RxCalendar,
  RxCardStack,
  RxDashboard,
  RxGroup,
  RxLayers,
  RxListBullet,
  RxMagicWand,
  RxMix,
  RxPerson,
  RxSketchLogo,
  RxStar,
} from "react-icons/rx";
import { TbTruckDelivery } from "react-icons/tb";
import { BsCartX } from "react-icons/bs";
import { MdListAlt } from "react-icons/md";
import { SiFig } from "react-icons/si";
import { BiPurchaseTagAlt } from "react-icons/bi";
import { MENU_VISIBILITY_CONFIG } from "../../config/menuVisibility";
import { getMenuPathname } from "../../helpers/MenuPathHelper";
import { RecommendationRunProvider } from "../../contexts/RecommendationRunContext";

const { Content, Sider } = LayoutAntd;

export enum SelectedMenuOptions {
  Dashboard = "dashboard",
  Admin = "admin",
  Pricings = "pricings",
  Promotions = "promotions",
  PurchaseOrders = "purchaseorders",
  RepositionOrders = "repositionorders",
  Fidelity = "fidelity",
  CommercialAnalysis = "commercialanalysis",
}

interface BusinessUnitContextType {
  contextValue: string | null;
  updateContextValue: (newValue: string) => void;
  refreshBusinessUnits: () => void;
}
export const BusinessUniteContext =
  React.createContext<BusinessUnitContextType>({
    contextValue: "",
    updateContextValue: () => {},
    refreshBusinessUnits: () => {},
  });

function LayoutContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedMenu: selectedNavBarMenu,
    setSelectedMenu: setSelectedNavBarMenu,
  } = useMenu();

  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const [businessUnitContextValue, setBusinessUnitContextValue] = useState<
    string | null
  >(null);
  const [isAdminOrg, setIsAdminOrg] = useState(false);
  const [hasWorkingOrg, setHasWorkingOrg] = useState(hasWorkingOrganization());
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>([]);
  const isAdminShell = isAdminOrg && !hasWorkingOrg;

  useEffect(() => {
    const checkAdminOrg = async () => {
      const organizationName = await getOrganizationBySubdomain();
      setIsAdminOrg(organizationName === "admin");
      setHasWorkingOrg(hasWorkingOrganization());
    };
    checkAdminOrg();
  }, []);

  useEffect(() => {
    if (isAdminShell && selectedNavBarMenu !== SelectedMenuOptions.Admin) {
      setSelectedNavBarMenu(SelectedMenuOptions.Admin);
    }
  }, [isAdminShell, selectedNavBarMenu, setSelectedNavBarMenu]);

  useEffect(() => {
    const globalAdminPaths = [
      "/admin/overview",
      "/admin/organizations",
      "/admin/client-validation-stats",
      "/admin/users",
      "/admin/users/create",
    ];
    const isGlobalAdminPath = globalAdminPaths.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
    if (isAdminShell && !isGlobalAdminPath) {
      navigate("/admin/overview", { replace: true });
    }
  }, [isAdminShell, location.pathname, navigate]);

  const updateBusinessUnitContextValue = (newValue: string) => {
    setBusinessUnitContextValue(newValue);
  };

  const refreshBusinessUnits = () => {
    window.dispatchEvent(new CustomEvent("refreshBusinessUnits"));
  };

  //choose the screen size
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  // create an event listener
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsMobileNavigationOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const pathname = getMenuPathname(location.pathname);
    const newOpenKeys: string[] = [];

    if (
      pathname.includes("/commercial-analysis") &&
      !pathname.includes("/commercial-analysis/loyalty-program")
    ) {
      newOpenKeys.push("submenu-bright-pulse");
    }

    if (
      pathname.includes("/commercial-intelligence/opportunities") ||
      pathname.includes("/commercial-intelligence/segments") ||
      pathname.includes("/commercial-intelligence/campaigns") ||
      pathname.includes("/fidelity/rewards") ||
      (pathname.includes("/promotions") &&
        !pathname.includes("/campaigns") &&
        !pathname.includes("/tickets") &&
        !pathname.includes("/clients") &&
        !pathname.includes("/clientsegments") &&
        !pathname.includes("/statistics") &&
        !pathname.includes("/dashboard"))
    ) {
      newOpenKeys.push("submenu-bright-actions");
    }

    if (
      pathname.includes("/commercial-analysis/loyalty-program") ||
      pathname.includes(
        "/commercial-intelligence/configuration/client-levels"
      ) ||
      pathname.includes("/commercial-intelligence/configuration/units")
    ) {
      newOpenKeys.push("submenu-bright-club");
    }

    if (
      pathname.includes(
        "/commercial-intelligence/configuration/reward-types"
      ) ||
      pathname.includes(
        "/commercial-intelligence/configuration/article-groups"
      ) ||
      pathname.includes("/promotions/clientlevels") ||
      pathname.includes("/fidelity/rewardtypes") ||
      pathname.includes("/fidelity/units") ||
      pathname.includes("/promotions/articlegroups")
    ) {
      newOpenKeys.push("submenu-configuration");
    }

    if (
      pathname.includes("/admin/parameters") ||
      pathname.includes("/admin/goals") ||
      pathname.includes("/admin/acceptedpaymentmethods") ||
      pathname.includes("/admin/wholesalers")
    ) {
      newOpenKeys.push("submenu-Parameters");
    }

    if (pathname.startsWith("/admin/") && pathname.includes("import")) {
      newOpenKeys.push("submenu-importar");
    }

    setMenuOpenKeys(newOpenKeys);
  }, [location.pathname]);

  const menuPromotions = (
    <>
      {MENU_VISIBILITY_CONFIG.promotionsMenu.dashboard && (
        <Menu.Item
          key="promotionDashboard"
          onClick={() => navigate("/promotions/dashboard")}
          icon={<RxDashboard />}
        >
          {t("NavBar-Dashboard")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.clients &&
        hasPermission(privilegesEnum.READ_CLIENTS) && (
          <Menu.Item
            key="clients"
            onClick={() => navigate("/promotions/clients")}
            icon={<RxPerson />}
          >
            {t("NavBar-Clients")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.segments &&
        hasPermission(privilegesEnum.READ_CLIENTS) && (
          <Menu.Item
            key="segments"
            onClick={() => navigate("/promotions/clientsegments")}
            icon={<RxGroup />}
          >
            {t("NavBar-ClientSegments")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.clientLevels &&
        hasPermission(privilegesEnum.READ_CLIENT_LEVEL) && (
          <Menu.Item
            onClick={() => navigate("/promotions/clientlevels")}
            key="clientLevels"
            icon={<RxLayers />}
          >
            {t("NavBar-ClientLevels")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.articleGroups &&
        hasPermission(privilegesEnum.READ_ARTICLE_GROUP) && (
          <Menu.Item
            key="promotions-articleGroups"
            onClick={() => navigate("/promotions/articlegroups")}
            icon={<RxBox />}
          >
            {t("NavBar-ArticleGroups")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.promotions &&
        hasPermission(privilegesEnum.READ_PROMOTIONS) && (
          <Menu.Item
            key="promos"
            onClick={() => navigate("/promotions")}
            icon={<RxStar />}
          >
            {t("NavBar-Promotions")}
          </Menu.Item>
        )}

      {MENU_VISIBILITY_CONFIG.promotionsMenu.tickets &&
        hasPermission(privilegesEnum.READ_TICKETS) && (
          <Menu.Item
            key="tickets"
            onClick={() => navigate("/promotions/tickets")}
            icon={<RxCardStack />}
          >
            {t("Common-Tickets")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.campaigns && (
        <Menu.Item
          key="campaigns"
          onClick={() => navigate("/promotions/campaigns")}
          icon={<RxMagicWand />}
        >
          {t("NavBar-Campaigns")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.promotionsMenu.statistics && (
        <Menu.Item
          key="statistics"
          onClick={() => navigate("/promotions/statistics")}
          icon={<RxBarChart />}
        >
          {t("NavBar-Statistics")}
        </Menu.Item>
      )}
    </>
  );

  const menuFidelity = (
    <>
      {MENU_VISIBILITY_CONFIG.fidelityMenu.dashboard && (
        <Menu.Item
          key="fidelityDashboard"
          onClick={() => navigate("/fidelity/dashboard")}
          icon={<RxDashboard />}
        >
          {t("NavBar-Dashboard")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.clients &&
        hasPermission(privilegesEnum.READ_CLIENTS) && (
          <Menu.Item
            key="clients"
            onClick={() => navigate("/fidelity/clients")}
            icon={<RxPerson />}
          >
            {t("NavBar-Clients")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.clientSegments &&
        hasPermission(privilegesEnum.READ_CLIENTS) && (
          <Menu.Item
            key="clientsegments"
            onClick={() => navigate("/fidelity/clientsegments")}
            icon={<RxGroup />}
          >
            {t("NavBar-ClientSegments")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.clientLevels &&
        hasPermission(privilegesEnum.READ_CLIENT_LEVEL) && (
          <Menu.Item
            onClick={() => navigate("/fidelity/clientlevels")}
            key="clientLevels"
            icon={<RxLayers />}
          >
            {t("NavBar-ClientLevels")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.tickets &&
        hasPermission(privilegesEnum.READ_TICKETS) && (
          <Menu.Item
            key="tickets"
            onClick={() => navigate("/fidelity/tickets")}
            icon={<RxCardStack />}
          >
            {t("Common-Tickets")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.recommendations && (
        <Menu.Item
          key="recommendations"
          onClick={() => navigate("/fidelity/recommendations")}
          icon={<RxMagicWand />}
        >
          {t("NavBar-Recommendations")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.rewardTypes && (
        <Menu.Item
          key="rewardtypes"
          onClick={() => navigate("/fidelity/rewardtypes")}
          icon={<RxListBullet />}
        >
          {t("NavBar-RewardTypes")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.rewards && (
        <Menu.Item
          key="rewards"
          onClick={() => navigate("/fidelity/rewards")}
          icon={<RxSketchLogo />}
        >
          {t("NavBar-Rewards")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.fidelityMenu.units && (
        <Menu.Item
          key="units"
          onClick={() => navigate("/fidelity/units")}
          icon={<RxStar />}
        >
          {t("NavBar-Units")}
        </Menu.Item>
      )}
    </>
  );

  const menuCommercialIntelligence = (
    <>
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.dashboard &&
        hasPermission(privilegesEnum.READ_COMMERCIAL_DASHBOARD) && (
          <Menu.Item
            key="commercialIntelligenceDashboard"
            onClick={() => navigate("/commercial-intelligence/dashboard")}
            icon={<RxDashboard />}
          >
            {t("NavBar-Dashboard")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
        .enabled && (
        <Menu.SubMenu
          key="submenu-bright-pulse"
          icon={<RxBarChart />}
          title={t("NavBar-BrightPulse")}
        >
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.commercialSummary &&
            hasPermission(privilegesEnum.READ_COMMERCIAL_SUMMARY) && (
              <Menu.Item
                key="commercialSummary"
                onClick={() => navigate("/commercial-analysis/summary")}
              >
                {t("NavBar-CommercialSummary")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.dailyControl &&
            hasPermission(privilegesEnum.READ_DAILY_CONTROL) && (
              <Menu.Item
                key="dailyControl"
                onClick={() => navigate("/commercial-analysis/daily-control")}
              >
                {t("NavBar-DailyControl")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.categoryAnalysis &&
            hasPermission(privilegesEnum.READ_CATEGORY_ANALYSIS) && (
              <Menu.Item
                key="categoryAnalysis"
                onClick={() => navigate("/commercial-analysis/categories")}
              >
                {t("NavBar-CategoryAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.productAnalysis &&
            hasPermission(privilegesEnum.READ_PRODUCT_ANALYSIS) && (
              <Menu.Item
                key="productAnalysis"
                onClick={() => navigate("/commercial-analysis/products")}
              >
                {t("NavBar-ProductAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.brandAnalysis &&
            hasPermission(privilegesEnum.READ_BRAND_ANALYSIS) && (
              <Menu.Item
                key="brandAnalysis"
                onClick={() => navigate("/commercial-analysis/brands")}
              >
                {t("NavBar-BrandAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.channelAnalysis &&
            hasPermission(privilegesEnum.READ_CHANNEL_ANALYSIS) && (
              <Menu.Item
                key="channelAnalysis"
                onClick={() => navigate("/commercial-analysis/channels")}
              >
                {t("NavBar-ChannelAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.storeAnalysis &&
            hasPermission(privilegesEnum.READ_STORE_ANALYSIS) && (
              <Menu.Item
                key="storeAnalysis"
                onClick={() => navigate("/commercial-analysis/stores")}
              >
                {t("NavBar-StoreAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.commercialDateAnalysis &&
            hasPermission(privilegesEnum.READ_COMMERCIAL_DATE_ANALYSIS) && (
              <Menu.Item
                key="commercialDateAnalysis"
                onClick={() =>
                  navigate("/commercial-analysis/commercial-dates")
                }
              >
                {t("NavBar-CommercialDateAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.productMixAnalysis &&
            hasPermission(privilegesEnum.READ_PRODUCT_MIX_ANALYSIS) && (
              <Menu.Item
                key="productMixAnalysis"
                onClick={() => navigate("/commercial-analysis/product-mix")}
              >
                {t("NavBar-ProductMixAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.clientAnalysis &&
            hasPermission(privilegesEnum.READ_CLIENT_ANALYSIS) && (
              <Menu.Item
                key="clientAnalysis"
                onClick={() => navigate("/commercial-analysis/clients")}
              >
                {t("NavBar-ClientAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.promotionAnalysis &&
            hasPermission(privilegesEnum.READ_PROMOTION_ANALYSIS) && (
              <Menu.Item
                key="promotionAnalysis"
                onClick={() => navigate("/commercial-analysis/promotions")}
              >
                {t("NavBar-PromotionAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.campaignAnalysis &&
            hasPermission(privilegesEnum.READ_CAMPAIGN_ANALYSIS) && (
              <Menu.Item
                key="campaignAnalysis"
                onClick={() => navigate("/commercial-analysis/campaigns")}
              >
                {t("NavBar-CampaignAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightPulse
            .subItems.wholesaleAnalysis &&
            hasPermission(privilegesEnum.READ_WHOLESALE_ANALYSIS) && (
              <Menu.Item
                key="wholesaleAnalysis"
                onClick={() => navigate("/commercial-analysis/wholesale")}
              >
                {t("NavBar-WholesaleAnalysis")}
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightActions
        .enabled && (
        <Menu.SubMenu
          key="submenu-bright-actions"
          icon={<RxStar />}
          title={t("NavBar-BrightActions")}
        >
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightActions
            .subItems.segments &&
            hasPermission(privilegesEnum.READ_CLIENTS) && (
              <Menu.Item
                key="segments"
                onClick={() => navigate("/commercial-intelligence/segments")}
              >
                {t("NavBar-ClientSegments")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightActions
            .subItems.incentives && (
            <Menu.Item
              key="incentives"
              onClick={() =>
                navigate("/commercial-intelligence/opportunities/incentives")
              }
            >
              {t("NavBar-Incentives")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightActions
            .subItems.promotions &&
            hasPermission(privilegesEnum.READ_PROMOTIONS) && (
              <Menu.Item
                key="promotions"
                onClick={() =>
                  navigate("/commercial-intelligence/opportunities/promotions")
                }
              >
                {t("NavBar-Promotions")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightActions
            .subItems.activationActions && (
            <Menu.Item
              key="activation-actions"
              onClick={() =>
                navigate(
                  "/commercial-intelligence/opportunities/activation-actions"
                )
              }
            >
              {t("NavBar-ActivationActions")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightActions
            .subItems.campaigns && (
            <Menu.Item
              key="campaigns"
              onClick={() => navigate("/commercial-intelligence/campaigns")}
            >
              {t("NavBar-Campaigns")}
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightClub.enabled && (
        <Menu.SubMenu
          key="submenu-bright-club"
          icon={<RxSketchLogo />}
          title={t("NavBar-BrightClub")}
        >
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightClub.subItems
            .loyaltyProgramAnalysis &&
            hasPermission(privilegesEnum.READ_LOYALTY_ANALYSIS) && (
              <Menu.Item
                key="loyaltyProgramAnalysis"
                onClick={() =>
                  navigate("/commercial-analysis/loyalty-program")
                }
              >
                {t("NavBar-LoyaltyProgramAnalysis")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightClub.subItems
            .clientLevels &&
            hasPermission(privilegesEnum.READ_CLIENT_LEVEL) && (
              <Menu.Item
                key="clientLevels"
                onClick={() =>
                  navigate(
                    "/commercial-intelligence/configuration/client-levels"
                  )
                }
              >
                {t("NavBar-ClientLevels")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.brightClub.subItems
            .units && (
            <Menu.Item
              key="units"
              onClick={() =>
                navigate("/commercial-intelligence/configuration/units")
              }
            >
              {t("Common-AccumulateUnits")}
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.similarArticles &&
        hasPermission(privilegesEnum.READ_PRODUCT_ANALYSIS) &&
        hasPermission(privilegesEnum.READ_ARTICLES) && (
          <Menu.Item
            key="similarArticles"
            onClick={() =>
              navigate("/commercial-intelligence/similar-articles")
            }
            icon={<RxMagicWand />}
          >
            {t("NavBar-SimilarArticles")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.clients &&
        hasPermission(privilegesEnum.READ_CLIENTS) && (
          <Menu.Item
            key="clients"
            onClick={() => navigate("/commercial-intelligence/clients")}
            icon={<RxPerson />}
          >
            {t("NavBar-Clients")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.tickets &&
        hasPermission(privilegesEnum.READ_TICKETS) && (
          <Menu.Item
            key="tickets"
            onClick={() => navigate("/commercial-intelligence/tickets")}
            icon={<RxCardStack />}
          >
            {t("Common-Tickets")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.configuration
        .enabled && (
        <Menu.SubMenu
          key="submenu-configuration"
          icon={<IoSettingsOutline />}
          title={t("NavBar-Configuration")}
        >
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.configuration
            .subItems.rewardTypes && (
            <Menu.Item
              key="rewardtypes"
              onClick={() =>
                navigate("/commercial-intelligence/configuration/reward-types")
              }
            >
              {t("NavBar-RewardTypes")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.commercialIntelligenceMenu.configuration
            .subItems.articleGroups &&
            hasPermission(privilegesEnum.READ_ARTICLE_GROUP) && (
              <Menu.Item
                key="articleGroups"
                onClick={() =>
                  navigate(
                    "/commercial-intelligence/configuration/article-groups"
                  )
                }
              >
                {t("NavBar-ArticleGroups")}
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
    </>
  );

  const menuPurchaseOrders = (
    <>
      {MENU_VISIBILITY_CONFIG.purchaseOrdersMenu.dashboard && (
        <Menu.Item
          className={styles.menuItem}
          key="purchaseordersDashboard"
          onClick={() => navigate("/purchaseorders/dashboard")}
          icon={<RxDashboard />}
        >
          {t("NavBar-Dashboard")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.purchaseOrdersMenu.catalogue && (
        <Menu.Item
          className={styles.menuItem}
          key="purchaseordersCatalogue"
          onClick={() => navigate("/purchaseorders/catalogue")}
          icon={<GrCatalog />}
        >
          {t("NavBar-Catalogue")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.purchaseOrdersMenu.management && (
        <Menu.Item
          className={styles.menuItem}
          key="purchaseordersManagement"
          onClick={() => navigate("/purchaseorders/management")}
          icon={<BiPurchaseTagAlt />}
        >
          {t("NavBar-PurchaseOrders")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.purchaseOrdersMenu.stockBreaks && (
        <Menu.Item
          className={styles.menuItem}
          key="purchaseordersStockbreaks"
          onClick={() => navigate("/purchaseorders/stockbreaks")}
          icon={<BsCartX />}
        >
          {t("NavBar-StockBreaks")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.purchaseOrdersMenu.recommendations && (
        <Menu.Item
          className={styles.menuItem}
          key="purchaseordersRecomendations"
          onClick={() => navigate("/purchaseorders/recomendations")}
          icon={<RxMagicWand />}
        >
          {"Recomendaciones"}
        </Menu.Item>
      )}
      {/*<Menu.Item*/}
      {/*  className={styles.menuItem}*/}
      {/*  key="purchaseordersReports"*/}
      {/*  onClick={() => navigate("/purchaseorders/reports")}*/}
      {/*  icon={<TbReportAnalytics />}*/}
      {/*>*/}
      {/*  {t("NavBar-Reports")}*/}
      {/*</Menu.Item>*/}
      {/*<Menu.Item*/}
      {/*  className={styles.menuItem}*/}
      {/*  key="purchaseordersRequests"*/}
      {/*  onClick={() => navigate("/purchaseorders/requests")}*/}
      {/*>*/}
      {/*  {t("NavBar-Requests")}*/}
      {/*</Menu.Item>*/}
    </>
  );

  const menuRepositionOrders = (
    <>
      {MENU_VISIBILITY_CONFIG.repositionOrdersMenu.dashboard && (
        <Menu.Item
          className={styles.menuItem}
          key="dashboard"
          onClick={() => navigate("/repositionorders/dashboard")}
          icon={<RxDashboard />}
        >
          {t("NavBar-Dashboard")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.repositionOrdersMenu.catalogue && (
        <Menu.Item
          className={styles.menuItem}
          key="catalogue"
          onClick={() => navigate("/repositionorders/catalogue")}
          icon={<GrCatalog />}
        >
          {t("NavBar-Catalogue")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.repositionOrdersMenu.management && (
        <Menu.Item
          className={styles.menuItem}
          key="management"
          onClick={() => navigate("/repositionorders/management")}
          icon={<TbTruckDelivery />}
        >
          {t("NavBar-RepositionOrders")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.repositionOrdersMenu.stockBreaks && (
        <Menu.Item
          className={styles.menuItem}
          key="stockbreaks"
          onClick={() => navigate("/repositionorders/stockbreaks")}
          icon={<BsCartX />}
        >
          {t("NavBar-StockBreaks")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.repositionOrdersMenu.requests && (
        <Menu.Item
          className={styles.menuItem}
          key="requests"
          onClick={() => navigate("/repositionorders/requests")}
          icon={<MdListAlt />}
        >
          {t("NavBar-Requests")}
        </Menu.Item>
      )}
      {/*<Menu.Item*/}
      {/*  className={styles.menuItem}*/}
      {/*  key="reports"*/}
      {/*  onClick={() => navigate("/repositionorders/reports")}*/}
      {/*  icon={<TbReportAnalytics />}*/}
      {/*>*/}
      {/*  {t("NavBar-Reports")}*/}
      {/*</Menu.Item>*/}
    </>
  );

  const menuPricing = (
    <>
      {MENU_VISIBILITY_CONFIG.pricingMenu.dashboard && (
        <Menu.Item
          key="dashboard"
          onClick={() => navigate("/pricings/dashboard")}
          icon={<RxDashboard />}
        >
          {t("NavBar-Dashboard")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.pricingMenu.pricings &&
        hasPermission(privilegesEnum.READ_PRICINGS) && (
          <Menu.Item
            key="pricings"
            onClick={() => navigate("/pricings")}
            icon={<RxBookmark />}
          >
            {t("NavBar-Pricings")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.pricingMenu.articleGroups &&
        hasPermission(privilegesEnum.READ_ARTICLE_GROUP) && (
          <Menu.Item
            key="pricings-articleGroups"
            onClick={() => navigate("/pricings/articlegroups")}
            icon={<RxBox />}
          >
            {t("NavBar-ArticleGroups")}
          </Menu.Item>
        )}
    </>
  );

  const menuAdmin = (
    <>
      {MENU_VISIBILITY_CONFIG.adminMenu.dashboard && (
        <Menu.Item
          className={styles.menuItem}
          key="adminDashboard"
          icon={<RxDashboard />}
          onClick={() => navigate("/admin/dashboard")}
        >
          {t("NavBar-Dashboard")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.adminMenu.company && (
        <Menu.Item
          className={styles.menuItem}
          key="submenu-company"
          icon={<FaRegBuilding />}
          onClick={() => navigate("/admin/businessrules")}
        >
          {t("NavBar-Company")}
        </Menu.Item>
      )}
      {MENU_VISIBILITY_CONFIG.adminMenu.parameters.enabled && (
        <Menu.SubMenu
          className={styles.menuItem}
          key="submenu-Parameters"
          icon={<IoSettingsOutline />}
          title={t("NavBar-Parameters")}
        >
          {MENU_VISIBILITY_CONFIG.adminMenu.parameters.subItems.parameters && (
            <Menu.Item
              key="parameters"
              onClick={() => navigate("/admin/parameters")}
            >
              {t("NavBar-Parameter")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.adminMenu.parameters.subItems.goals && (
            <Menu.Item key="goals" onClick={() => navigate("/admin/goals")}>
              {t("Common-Goals")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.adminMenu.parameters.subItems
            .paymentMethods &&
            hasPermission(privilegesEnum.READ_ACCEPTED_PAYMENT_METHODS) && (
              <Menu.Item
                key="paymentmethods"
                onClick={() => navigate("/admin/acceptedpaymentmethods")}
              >
                {t("NavBar-AcceptedPaymentMethods")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.parameters.subItems.wholesalers && (
            <Menu.Item
              key="wholesalers"
              onClick={() => navigate("/admin/wholesalers")}
            >
              {t("NavBar-Wholesalers")}
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.adminMenu.usersAndRoles &&
        (hasPermission(privilegesEnum.READ_USERS) ||
          hasPermission(privilegesEnum.READ_ROLES)) && (
          <Menu.Item
            key="users"
            onClick={() => navigate("/admin/users")}
            icon={<FiUsers />}
          >
            {t("NavBar-UsersAndRoles")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.enabled && (
        <Menu.SubMenu
          className={styles.menuItem}
          key="submenu-catalogue"
          icon={<GrCatalog />}
          title={t("NavBar-Catalogue")}
        >
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.categories &&
            hasPermission(privilegesEnum.READ_CATEGORIES) && (
              <Menu.Item
                key="categories"
                onClick={() => navigate("/admin/categories")}
              >
                {t("NavBar-Categories")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.articles &&
            hasPermission(privilegesEnum.READ_ARTICLES) && (
              <Menu.Item
                key="articles"
                onClick={() => navigate("/admin/articles")}
              >
                {t("NavBar-Articles")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.attributes &&
            hasPermission(privilegesEnum.READ_ATTRIBUTES) && (
              <Menu.Item
                key="attributes"
                onClick={() => navigate("/admin/attributes")}
              >
                {t("NavBar-Attributes")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.colours &&
            hasPermission(privilegesEnum.READ_COLOURS) && (
              <Menu.Item
                key="colours"
                onClick={() => navigate("/admin/colours")}
              >
                {t("NavBar-Colours")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.suppliers &&
            hasPermission(privilegesEnum.READ_SUPPLIERS) && (
              <Menu.Item
                key="suppliers"
                onClick={() => navigate("/admin/suppliers")}
              >
                {t("NavBar-Suppliers")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.brands &&
            hasPermission(privilegesEnum.READ_BRANDS) && (
              <Menu.Item key="brands" onClick={() => navigate("/admin/brands")}>
                {t("NavBar-Brands")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems.curves &&
            hasPermission(privilegesEnum.READ_CURVES) && (
              <Menu.Item key="curves" onClick={() => navigate("/admin/curves")}>
                {t("NavBar-Curves")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.catalogue.subItems
            .stockMovements && (
            <Menu.Item
              key="stockmovements"
              onClick={() => navigate("/admin/stockmovements")}
            >
              {t("Common-StockMovements")}
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.adminMenu.branchOffices.enabled && (
        <Menu.SubMenu
          className={styles.menuItem}
          key="submenu-branchoffices"
          icon={<LiaStoreSolid />}
          title={t("NavBar-BranchOffices")}
        >
          {MENU_VISIBILITY_CONFIG.adminMenu.branchOffices.subItems
            .branchOffices &&
            hasPermission(privilegesEnum.READ_BRANCH_OFFICES) && (
              <Menu.Item
                className={styles.menuItem}
                key="branchoffices"
                onClick={() => navigate("/admin/branchoffices")}
              >
                {t("NavBar-BranchOffices")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.branchOffices.subItems.deposits &&
            hasPermission(privilegesEnum.READ_DEPOSITS) && (
              <Menu.Item
                key="deposits"
                onClick={() => navigate("/admin/deposits")}
              >
                {t("NavBar-Deposits")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.branchOffices.subItems.clusters &&
            hasPermission(privilegesEnum.READ_CLUSTERS) && (
              <Menu.Item
                className={styles.menuItem}
                key="clusters"
                onClick={() => navigate("/admin/clusters")}
              >
                {t("NavBar-Clusters")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.branchOffices.subItems.vehicles &&
            hasPermission(privilegesEnum.READ_VEHICLES) && (
              <Menu.Item
                className={styles.menuItem}
                key="vehicles"
                onClick={() => navigate("/admin/vehicles")}
              >
                {t("NavBar-Vehicles")}
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.adminMenu.import.enabled && (
        <Menu.SubMenu
          className={styles.menuItem}
          key="submenu-importar"
          icon={<LuUpload />}
          title={t("Common-Import")}
          onTitleClick={() => navigate("/admin/imports")}
        >
          <Menu.Item
            className={styles.menuItem}
            key="importdashboard"
            onClick={() => navigate("/admin/imports")}
          >
            {t("ImportDashboard-Overview")}
          </Menu.Item>
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.ticketImport &&
            hasPermission(privilegesEnum.WRITE_TICKETS) && (
              <Menu.Item
                className={styles.menuItem}
                key="ticketimport"
                onClick={() => navigate("/admin/tickets/import")}
              >
                {t("Common-TicketImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.rewardImport && (
            <Menu.Item
              className={styles.menuItem}
              key="rewardimport"
              onClick={() => navigate("/admin/rewards/import")}
            >
              {t("Common-RewardImport")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems
            .stockMovementImport && (
            <Menu.Item
              className={styles.menuItem}
              key="stockmovementimport"
              onClick={() => navigate("/admin/stockmovements/import")}
            >
              {t("Common-StockMovementImport")}
            </Menu.Item>
          )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.pricingImport &&
            hasPermission(privilegesEnum.READ_PRICINGS) && (
              <Menu.Item
                className={styles.menuItem}
                key="pricingimport"
                onClick={() => navigate("/admin/pricings/import")}
              >
                {t("Common-PricingImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.clientImport &&
            hasPermission(privilegesEnum.WRITE_CLIENTS) && (
              <Menu.Item
                className={styles.menuItem}
                key="clientimport"
                onClick={() => navigate("/admin/clients/import")}
              >
                {t("Common-ClientImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.skuImport &&
            hasPermission(privilegesEnum.WRITE_ARTICLES) && (
              <Menu.Item
                className={styles.menuItem}
                key="skuimport"
                onClick={() => navigate("/admin/articles/import")}
              >
                {t("Common-SkuImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.articleSeasonImport &&
            hasPermission(privilegesEnum.WRITE_ARTICLES) && (
              <Menu.Item
                className={styles.menuItem}
                key="articleseasonimport"
                onClick={() => navigate("/admin/articles/seasons/import")}
              >
                {t("Common-ArticleSeasonImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.paymentMethodImport &&
            hasPermission(privilegesEnum.WRITE_ACCEPTED_PAYMENT_METHODS) && (
              <Menu.Item
                className={styles.menuItem}
                key="paymentmethodimport"
                onClick={() => navigate("/admin/acceptedpaymentmethods/import")}
              >
                {t("Common-PaymentMethodImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.promotionImport &&
            hasPermission(privilegesEnum.WRITE_PROMOTIONS) && (
              <Menu.Item
                className={styles.menuItem}
                key="promotionimport"
                onClick={() => navigate("/admin/promotions/import")}
              >
                {t("Common-PromotionImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.pointsMovementImport &&
            hasPermission(privilegesEnum.WRITE_CLIENTS) && (
              <Menu.Item
                className={styles.menuItem}
                key="pointsmovementimport"
                onClick={() => navigate("/admin/clients/points-movements/import")}
              >
                {t("Common-PointsMovementImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.branchOfficeImport &&
            hasPermission(privilegesEnum.WRITE_BRANCH_OFFICES) && (
              <Menu.Item
                className={styles.menuItem}
                key="branchofficeimport"
                onClick={() => navigate("/admin/branchoffices/import")}
              >
                {t("Common-BranchOfficeImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.purchaseOrderImport &&
            hasPermission(privilegesEnum.WRITE_PURCHASE_ORDERS) && (
              <Menu.Item
                className={styles.menuItem}
                key="purchaseorderimport"
                onClick={() => navigate("/admin/purchaseorders/import")}
              >
                {t("Common-PurchaseOrderImport")}
              </Menu.Item>
            )}
          {MENU_VISIBILITY_CONFIG.adminMenu.import.subItems.repositionOrderImport &&
            hasPermission(privilegesEnum.WRITE_REPOSITION_ORDERS) && (
              <Menu.Item
                className={styles.menuItem}
                key="repositionorderimport"
                onClick={() => navigate("/admin/repositionorders/import")}
              >
                {t("Common-RepositionOrderImport")}
              </Menu.Item>
            )}
        </Menu.SubMenu>
      )}
      {MENU_VISIBILITY_CONFIG.adminMenu.dataScienceJobs && isAdminOrg && (
        <Menu.Item
          className={styles.menuItem}
          key="datasciencejobs"
          onClick={() => navigate("/admin/datasciencejobs")}
          icon={<RxBarChart />}
        >
          {t("NavBar-DataScienceJobs")}
        </Menu.Item>
      )}
    </>
  );

  const menuGlobalAdmin = (
    <>
      <Menu.Item
        className={styles.menuItem}
        key="adminOverview"
        icon={<RxDashboard />}
        onClick={() => navigate("/admin/overview")}
      >
        {t("AdminNavigation-Overview")}
      </Menu.Item>
      {MENU_VISIBILITY_CONFIG.adminMenu.clientValidationStats && (
        <Menu.Item
          className={styles.menuItem}
          key="adminOrganizations"
          icon={<FaRegBuilding />}
          onClick={() => navigate("/admin/organizations")}
        >
          {t("AdminNavigation-Organizations")}
        </Menu.Item>
      )}
      {(hasPermission(privilegesEnum.READ_USERS) ||
        hasPermission(privilegesEnum.READ_ROLES)) && (
        <Menu.Item
          className={styles.menuItem}
          key="adminUsers"
          icon={<FiUsers />}
          onClick={() => navigate("/admin/users")}
        >
          {t("AdminNavigation-AdminUsers")}
        </Menu.Item>
      )}
    </>
  );

  const menuCommercialAnalysis = (
    <>
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.commercialSummary &&
        hasPermission(privilegesEnum.READ_COMMERCIAL_SUMMARY) && (
          <Menu.Item
            key="commercialSummary"
            onClick={() => navigate("/commercial-analysis/summary")}
            icon={<RxDashboard />}
          >
            {t("NavBar-CommercialSummary")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.dailyControl &&
        hasPermission(privilegesEnum.READ_DAILY_CONTROL) && (
          <Menu.Item
            key="dailyControl"
            onClick={() => navigate("/commercial-analysis/daily-control")}
            icon={<RxListBullet />}
          >
            {t("NavBar-DailyControl")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.categoryAnalysis &&
        hasPermission(privilegesEnum.READ_CATEGORY_ANALYSIS) && (
          <Menu.Item
            key="categoryAnalysis"
            onClick={() => navigate("/commercial-analysis/categories")}
            icon={<RxCardStack />}
          >
            {t("NavBar-CategoryAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.productAnalysis &&
        hasPermission(privilegesEnum.READ_PRODUCT_ANALYSIS) && (
          <Menu.Item
            key="productAnalysis"
            onClick={() => navigate("/commercial-analysis/products")}
            icon={<RxBox />}
          >
            {t("NavBar-ProductAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.brandAnalysis &&
        hasPermission(privilegesEnum.READ_BRAND_ANALYSIS) && (
          <Menu.Item
            key="brandAnalysis"
            onClick={() => navigate("/commercial-analysis/brands")}
            icon={<RxBookmark />}
          >
            {t("NavBar-BrandAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.channelAnalysis &&
        hasPermission(privilegesEnum.READ_CHANNEL_ANALYSIS) && (
          <Menu.Item
            key="channelAnalysis"
            onClick={() => navigate("/commercial-analysis/channels")}
            icon={<RxLayers />}
          >
            {t("NavBar-ChannelAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.storeAnalysis &&
        hasPermission(privilegesEnum.READ_STORE_ANALYSIS) && (
          <Menu.Item
            key="storeAnalysis"
            onClick={() => navigate("/commercial-analysis/stores")}
            icon={<LiaStoreSolid />}
          >
            {t("NavBar-StoreAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.commercialDateAnalysis &&
        hasPermission(privilegesEnum.READ_COMMERCIAL_DATE_ANALYSIS) && (
          <Menu.Item
            key="commercialDateAnalysis"
            onClick={() => navigate("/commercial-analysis/commercial-dates")}
            icon={<RxCalendar />}
          >
            {t("NavBar-CommercialDateAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.productMixAnalysis &&
        hasPermission(privilegesEnum.READ_PRODUCT_MIX_ANALYSIS) && (
          <Menu.Item
            key="productMixAnalysis"
            onClick={() => navigate("/commercial-analysis/product-mix")}
            icon={<RxMix />}
          >
            {t("NavBar-ProductMixAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.clientAnalysis &&
        hasPermission(privilegesEnum.READ_CLIENT_ANALYSIS) && (
          <Menu.Item
            key="clientAnalysis"
            onClick={() => navigate("/commercial-analysis/clients")}
            icon={<RxPerson />}
          >
            {t("NavBar-ClientAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.promotionAnalysis &&
        hasPermission(privilegesEnum.READ_PROMOTION_ANALYSIS) && (
          <Menu.Item
            key="promotionAnalysis"
            onClick={() => navigate("/commercial-analysis/promotions")}
            icon={<RxSketchLogo />}
          >
            {t("NavBar-PromotionAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.campaignAnalysis &&
        hasPermission(privilegesEnum.READ_CAMPAIGN_ANALYSIS) && (
          <Menu.Item
            key="campaignAnalysis"
            onClick={() => navigate("/commercial-analysis/campaigns")}
            icon={<RxStar />}
          >
            {t("NavBar-CampaignAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.loyaltyProgramAnalysis &&
        hasPermission(privilegesEnum.READ_LOYALTY_ANALYSIS) && (
          <Menu.Item
            key="loyaltyProgramAnalysis"
            onClick={() => navigate("/commercial-analysis/loyalty-program")}
            icon={<BiPurchaseTagAlt />}
          >
            {t("NavBar-LoyaltyProgramAnalysis")}
          </Menu.Item>
        )}
      {MENU_VISIBILITY_CONFIG.commercialAnalysisMenu.wholesaleAnalysis &&
        hasPermission(privilegesEnum.READ_WHOLESALE_ANALYSIS) && (
          <Menu.Item
            key="wholesaleAnalysis"
            onClick={() => navigate("/commercial-analysis/wholesale")}
            icon={<RxBarChart />}
          >
            {t("NavBar-WholesaleAnalysis")}
          </Menu.Item>
        )}
    </>
  );

  const menuItems = isAdminShell
    ? menuGlobalAdmin
    : selectedNavBarMenu === SelectedMenuOptions.Admin
      ? menuAdmin
      : selectedNavBarMenu === SelectedMenuOptions.Promotions
      ? menuCommercialIntelligence
      : selectedNavBarMenu === SelectedMenuOptions.Fidelity
      ? menuFidelity
      : selectedNavBarMenu === SelectedMenuOptions.Pricings
      ? menuPricing
      : selectedNavBarMenu === SelectedMenuOptions.RepositionOrders
      ? menuRepositionOrders
      : selectedNavBarMenu === SelectedMenuOptions.PurchaseOrders
      ? menuPurchaseOrders
      : selectedNavBarMenu === SelectedMenuOptions.CommercialAnalysis
      ? menuCommercialAnalysis
      : undefined;

  const selectedModuleTitle =
    selectedNavBarMenu === SelectedMenuOptions.Admin
      ? t("NavBar-Admin")
      : selectedNavBarMenu === SelectedMenuOptions.Pricings
      ? t("NavBar-Pricings")
      : selectedNavBarMenu === SelectedMenuOptions.Promotions
      ? t("NavBar-CommercialIntelligence")
      : selectedNavBarMenu === SelectedMenuOptions.Fidelity
      ? t("NavBar-Fidelity")
      : selectedNavBarMenu === SelectedMenuOptions.PurchaseOrders
      ? t("NavBar-Shopping")
      : selectedNavBarMenu === SelectedMenuOptions.RepositionOrders
      ? t("NavBar-Surtido")
      : selectedNavBarMenu === SelectedMenuOptions.CommercialAnalysis
      ? t("NavBar-CommercialAnalysis")
      : t("Common-Menu");

  const getSelectedKeys = (): string[] => {
    const pathname = getMenuPathname(location.pathname);

    if (pathname === "/admin/imports") return ["importdashboard"];
    if (pathname.includes("/admin/overview")) return ["adminOverview"];
    if (
      pathname.includes("/admin/organizations") ||
      pathname.includes("/admin/client-validation-stats")
    )
      return ["adminOrganizations"];
    if (isAdminShell && pathname.includes("/admin/users"))
      return ["adminUsers"];
    if (pathname.includes("/clients/points-movements/import"))
      return ["pointsmovementimport"];
    if (pathname.includes("/clients/import")) return ["clientimport"];
    if (pathname.includes("/articles/import")) return ["skuimport"];
    if (pathname.includes("/acceptedpaymentmethods/import"))
      return ["paymentmethodimport"];
    if (pathname.includes("/promotions/import")) return ["promotionimport"];
    if (pathname.includes("/branchoffices/import"))
      return ["branchofficeimport"];
    if (pathname.includes("/purchaseorders/import"))
      return ["purchaseorderimport"];
    if (pathname.includes("/repositionorders/import"))
      return ["repositionorderimport"];

    if (pathname.includes("/dashboard")) {
      if (pathname.includes("/admin")) return ["adminDashboard"];
      if (pathname.includes("/pricings")) return ["dashboard"];
      if (pathname.includes("/commercial-intelligence"))
        return ["commercialIntelligenceDashboard"];
      if (pathname.includes("/promotions"))
        return ["commercialIntelligenceDashboard"];
      if (pathname.includes("/fidelity")) return ["fidelityDashboard"];
      if (pathname.includes("/purchaseorders"))
        return ["purchaseordersDashboard"];
      if (pathname.includes("/repositionorders")) return ["dashboard"];
    }

    if (pathname.includes("/pricings")) {
      if (pathname.includes("/articlegroups"))
        return ["pricings-articleGroups"];
      if (!pathname.includes("/dashboard")) return ["pricings"];
    }

    if (pathname.includes("/commercial-analysis")) {
      if (pathname.includes("/daily-control")) return ["dailyControl"];
      if (pathname.includes("/summary")) return ["commercialSummary"];
      if (pathname.includes("/categories")) return ["categoryAnalysis"];
      if (pathname.includes("/products")) return ["productAnalysis"];
      if (pathname.includes("/brands")) return ["brandAnalysis"];
      if (pathname.includes("/channels")) return ["channelAnalysis"];
      if (pathname.includes("/stores")) return ["storeAnalysis"];
      if (pathname.includes("/commercial-dates"))
        return ["commercialDateAnalysis"];
      if (pathname.includes("/product-mix")) return ["productMixAnalysis"];
      if (pathname.includes("/clients")) return ["clientAnalysis"];
      if (pathname.includes("/promotions")) return ["promotionAnalysis"];
      if (pathname.includes("/campaigns")) return ["campaignAnalysis"];
      if (pathname.includes("/loyalty-program"))
        return ["loyaltyProgramAnalysis"];
      if (pathname.includes("/wholesale")) return ["wholesaleAnalysis"];
    }

    if (pathname.includes("/commercial-intelligence")) {
      if (pathname.includes("/opportunities/incentives")) return ["incentives"];
      if (pathname.includes("/opportunities/promotions")) return ["promotions"];
      if (pathname.includes("/opportunities/activation-actions"))
        return ["activation-actions"];
      if (pathname.includes("/campaigns")) return ["campaigns"];
      if (pathname.includes("/similar-articles")) return ["similarArticles"];
      if (pathname.includes("/clients")) return ["clients"];
      if (pathname.includes("/segments")) return ["segments"];
      if (pathname.includes("/tickets")) return ["tickets"];
      if (pathname.includes("/configuration/client-levels"))
        return ["clientLevels"];
      if (pathname.includes("/configuration/reward-types"))
        return ["rewardtypes"];
      if (pathname.includes("/configuration/units")) return ["units"];
      if (pathname.includes("/configuration/article-groups"))
        return ["articleGroups"];
    }

    if (pathname.includes("/promotions")) {
      if (pathname.includes("/clientsegments")) return ["segments"];
      if (pathname.includes("/clients")) return ["clients"];
      if (pathname.includes("/clientlevels")) return ["clientLevels"];
      if (pathname.includes("/articlegroups")) return ["articleGroups"];
      if (pathname.includes("/tickets")) return ["tickets"];
      if (pathname.includes("/campaigns")) return ["campaigns"];
      if (pathname.includes("/statistics")) return ["statistics"];
      if (
        !pathname.includes("/dashboard") &&
        !pathname.includes("/campaigns") &&
        !pathname.includes("/tickets") &&
        !pathname.includes("/clients") &&
        !pathname.includes("/clientsegments") &&
        !pathname.includes("/clientlevels") &&
        !pathname.includes("/articlegroups") &&
        !pathname.includes("/statistics")
      )
        return ["promotions"];
    }

    if (pathname.includes("/fidelity")) {
      if (pathname.includes("/rewards")) return ["incentives"];
      if (pathname.includes("/rewardtypes")) return ["rewardtypes"];
      if (pathname.includes("/units")) return ["units"];
      if (pathname.includes("/clientsegments")) return ["clientsegments"];
      if (pathname.includes("/clients")) return ["clients"];
      if (pathname.includes("/clientlevels")) return ["clientLevels"];
      if (pathname.includes("/tickets")) return ["tickets"];
      if (pathname.includes("/recommendations")) return ["recommendations"];
    }

    if (pathname.includes("/purchaseorders")) {
      if (pathname.includes("/catalogue")) return ["purchaseordersCatalogue"];
      if (pathname.includes("/management")) return ["purchaseordersManagement"];
      if (pathname.includes("/stockbreaks"))
        return ["purchaseordersStockbreaks"];
      if (pathname.includes("/recomendations"))
        return ["purchaseordersRecomendations"];
    }

    if (pathname.includes("/repositionorders")) {
      if (pathname.includes("/catalogue")) return ["catalogue"];
      if (pathname.includes("/management")) return ["management"];
      if (pathname.includes("/stockbreaks")) return ["stockbreaks"];
      if (pathname.includes("/requests")) return ["requests"];
    }

    if (pathname.includes("/admin")) {
      if (pathname.includes("/businessrules")) return ["submenu-company"];
      if (pathname.includes("/parameters")) return ["parameters"];
      if (pathname.includes("/goals")) return ["goals"];
      if (pathname.includes("/acceptedpaymentmethods"))
        return ["paymentmethods"];
      if (pathname.includes("/wholesalers")) return ["wholesalers"];
      if (pathname.includes("/users")) return ["users"];
      if (pathname.includes("/categories")) return ["categories"];
      if (pathname.includes("/articles")) return ["articles"];
      if (pathname.includes("/attributes")) return ["attributes"];
      if (pathname.includes("/colours")) return ["colours"];
      if (pathname.includes("/suppliers")) return ["suppliers"];
      if (pathname.includes("/brands")) return ["brands"];
      if (pathname.includes("/curves")) return ["curves"];
      if (pathname.includes("/stockmovements")) return ["stockmovements"];
      if (pathname.includes("/branchoffices")) return ["branchoffices"];
      if (pathname.includes("/deposits")) return ["deposits"];
      if (pathname.includes("/clusters")) return ["clusters"];
      if (pathname.includes("/vehicles")) return ["vehicles"];
      if (pathname.includes("/tickets/import")) return ["ticketimport"];
      if (pathname.includes("/rewards/import")) return ["rewardimport"];
      if (pathname.includes("/stockmovements/import"))
        return ["stockmovementimport"];
      if (pathname.includes("/pricings/import")) return ["pricingimport"];
      if (pathname.includes("/datasciencejobs")) return ["datasciencejobs"];
    }

    return [];
  };

  const selectedKeys = getSelectedKeys();

  const context: BusinessUnitContextType = {
    contextValue: businessUnitContextValue,
    updateContextValue: updateBusinessUnitContextValue,
    refreshBusinessUnits: refreshBusinessUnits,
  };

  return (
    <BusinessUniteContext.Provider value={context}>
      <LayoutAntd style={{ minHeight: "100vh" }}>
        {!isMobile && (
          <Sider
            className={styles.sider}
            collapsible={
              window.matchMedia("screen and (min-width: 812px)").matches
            }
            collapsed={collapsed}
            onCollapse={(value) => setCollapsed(value)}
            trigger={
              <div>
                {!collapsed ? (
                  <div className={styles.expandedWrapper}>
                    <SiFig />
                    <Typography.Text>{t("Common-Collapse")}</Typography.Text>
                  </div>
                ) : (
                  <div className={styles.collapsedButton}>
                    <SiFig />
                  </div>
                )}
              </div>
            }
          >
            <Space onClick={() => navigate("/")}>
              <img
                className={styles.logo}
                src={collapsed ? logoSmall : logo}
                alt="Logo"
              />
            </Space>

            <Menu
              mode="inline"
              className={styles.menu}
              selectedKeys={selectedKeys}
              openKeys={menuOpenKeys}
              onOpenChange={(keys) => setMenuOpenKeys(keys as string[])}
            >
              {menuItems}
            </Menu>
          </Sider>
        )}
        <LayoutAntd className={styles.layoutAntd}>
          <div className={styles.wrap}>
            <NavBar
              onChangeMenu={setSelectedNavBarMenu}
              selectedMenu={selectedNavBarMenu}
              isAdminOrg={isAdminShell || undefined}
              onOpenNavigation={
                isMobile && menuItems
                  ? () => setIsMobileNavigationOpen(true)
                  : undefined
              }
            />
            {isMobile && menuItems && (
              <Drawer
                title={selectedModuleTitle}
                visible={isMobileNavigationOpen}
                placement="left"
                onClose={() => setIsMobileNavigationOpen(false)}
                className={styles.mobileNavigationDrawer}
                width={320}
                destroyOnClose
              >
                <Menu
                  mode="inline"
                  className={styles.menu}
                  selectedKeys={selectedKeys}
                  openKeys={menuOpenKeys}
                  onClick={() => setIsMobileNavigationOpen(false)}
                  onOpenChange={(keys) => setMenuOpenKeys(keys as string[])}
                >
                  {menuItems}
                </Menu>
              </Drawer>
            )}
          </div>
          <Content className={styles.content}>
            <Outlet />
          </Content>
        </LayoutAntd>
      </LayoutAntd>
    </BusinessUniteContext.Provider>
  );
}

export function Layout() {
  return (
    <RecommendationRunProvider>
      <LayoutContent />
    </RecommendationRunProvider>
  );
}

export default Layout;
